/**
 * Industries the signup flow exposes for new tenants. Each industry maps to a
 * theme + initial-content seed function. The `available` flag is the launch
 * gate: only `available` industries can be picked in the UI; `coming_soon`
 * entries render but are disabled. Per
 * docs/development/sessions/2x-23-industry-onboard.md the launch baseline
 * needs at least `corporate` available; the other five are reserved slots
 * filled in by the dedicated theme-addition sessions (2x-11..2x-15).
 * Currently launched: all six industries — saga routes (`THEME_ROUTES` in
 * `apply-initial-theme.ts`) and CMS endpoints (`seed-{slug}-theme.ts`) are
 * wired for every entry. Flip `available: false` only when retiring an
 * industry pre-launch.
 *
 * `themeSlug` and `themeVersion` are forwarded into the provisioning saga
 * (`apps/admin/src/lib/server/provisioning/types.ts`) and become the value
 * stored on `theme-customizations.theme_slug` in Payload. Keep slugs stable
 * — they are used as the dispatch key in
 * `apps/admin/src/lib/server/provisioning/steps/apply-initial-theme.ts`.
 */

export const IndustryId = {
  Corporate: 'corporate',
  Restaurant: 'restaurant',
  Beauty: 'beauty',
  Medical: 'medical',
  Law: 'law',
  Retail: 'retail',
} as const;
export type IndustryId = (typeof IndustryId)[keyof typeof IndustryId];

export interface Industry {
  readonly id: IndustryId;
  readonly label: string;
  readonly description: string;
  /** Whether this industry can be picked at signup right now. */
  readonly available: boolean;
  readonly themeSlug: string;
  readonly themeVersion: string;
  /** Three placeholder slug examples for the slug-naming hint, lessons #1. */
  readonly slugExamples: readonly [string, string, string];
}

export const INDUSTRY_CATALOG: readonly Industry[] = [
  {
    id: 'corporate',
    label: 'コーポレート',
    description: '会社案内・サービス紹介・お問合せの 3 ページが揃います。',
    available: true,
    themeSlug: 'corporate',
    themeVersion: '1.0.0',
    slugExamples: ['acme-inc', 'sunrise-consulting', 'tanaka-design'],
  },
  {
    id: 'restaurant',
    label: '飲食',
    description: 'メニュー / アクセス / 予約フォームが入ります。',
    available: true,
    themeSlug: 'restaurant',
    themeVersion: '1.0.0',
    slugExamples: ['izakaya-tanaka', 'cafe-aoyama', 'sushi-yamada'],
  },
  {
    id: 'beauty',
    label: '美容',
    description: '施術メニュー / スタッフ紹介 / 作例ギャラリー / 予約 CTA が入ります。',
    available: true,
    themeSlug: 'beauty',
    themeVersion: '1.0.0',
    slugExamples: ['salon-akari', 'beauty-momoka', 'hair-tokyo'],
  },
  {
    id: 'medical',
    label: '医療',
    description: '診療科目 / 診療時間 / アクセスが入ります。',
    available: true,
    themeSlug: 'medical',
    themeVersion: '1.0.0',
    slugExamples: ['sakura-clinic', 'minato-dental', 'kobayashi-naika'],
  },
  {
    id: 'law',
    label: '士業',
    description: '取扱業務 / 料金表 / お問合せが入ります。',
    available: true,
    themeSlug: 'law',
    themeVersion: '1.0.0',
    slugExamples: ['ito-law', 'sato-shihoshoshi', 'tax-office-tokyo'],
  },
  {
    id: 'retail',
    label: '小売',
    description: '商品紹介 / 店舗情報 / オンラインショップ案内が入ります。',
    available: true,
    themeSlug: 'retail',
    themeVersion: '1.0.0',
    slugExamples: ['shop-mishima', 'select-shibuya', 'kyoto-zakka'],
  },
];

const INDUSTRY_BY_ID: Readonly<Record<IndustryId, Industry>> = Object.freeze(
  Object.fromEntries(INDUSTRY_CATALOG.map((it) => [it.id, it])) as Record<IndustryId, Industry>,
);

// Derived from INDUSTRY_CATALOG so the order is always consistent with the catalog.
// New industry sessions (2x-11..2x-15) add one entry to IndustryId + INDUSTRY_CATALOG
// and INDUSTRY_IDS auto-updates.
export const INDUSTRY_IDS = INDUSTRY_CATALOG.map((it) => it.id) as [IndustryId, ...IndustryId[]];

export function isIndustryId(value: unknown): value is IndustryId {
  return typeof value === 'string' && (INDUSTRY_IDS as readonly string[]).includes(value);
}

export function getIndustry(id: IndustryId): Industry {
  return INDUSTRY_BY_ID[id];
}

/** Lookup by id with `null` instead of throwing — used for untrusted input. */
export function findIndustry(id: string): Industry | null {
  if (!isIndustryId(id)) return null;
  return INDUSTRY_BY_ID[id];
}

/** True when the industry exists AND is selectable at signup right now. */
export function isAvailableIndustry(id: string): boolean {
  const it = findIndustry(id);
  return it !== null && it.available;
}

/** Default fallback used when nothing is selected — corporate per session 2x-theme-seeding. */
export const DEFAULT_INDUSTRY_ID: IndustryId = 'corporate';
